import React, { useState } from "react";
import { IconCircleCheckFilled } from "@tabler/icons-react";
import { Button, FieldError, Input, Select, Textarea } from "../utils/ui";
import { formatMoneda } from "../utils/format";

function toISODate(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addDays(dateStr, days) {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + days);
  return toISODate(d);
}

export default function SucursalForm({
  initial = null,
  initialSub = null,
  clientes = [],
  paquetes = [],
  defaultClientId = null,
  onSubmit,
  onCancel,
  onNeedClients,
  saving = false,
  serverError = null,
}) {
  const [clientId, setClientId] = useState(initial?.client_id ?? defaultClientId ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [address, setAddress] = useState(initial?.address ?? "");
  const [city, setCity] = useState(initial?.city ?? "");
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [status, setStatus] = useState(initial?.status ?? "active");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [packageId, setPackageId] = useState(
    initialSub?.package_id ? String(initialSub.package_id) : ""
  );
  const [paymentDate, setPaymentDate] = useState(initialSub?.payment_date ?? toISODate());
  const [nextPaymentDate, setNextPaymentDate] = useState(
    initialSub?.next_payment_date ?? addDays(toISODate(), 30)
  );
  const [error, setError] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) return setError("El nombre es obligatorio.");
    if (!clientId) return setError("Selecciona el cliente.");
    onSubmit({
      client_id: Number(clientId),
      name: name.trim(),
      address: address || null,
      city: city || null,
      phone: phone || null,
      status,
      notes: notes || null,
      ...(packageId
        ? {
            package_id: Number(packageId),
            payment_date: paymentDate || null,
            next_payment_date: nextPaymentDate || null,
          }
        : {}),
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      {clientes.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/70">
          Aún no tienes clientes registrados. Crea uno primero para poder asignar la sucursal.
          <div className="mt-3">
            <Button type="button" variant="secondary" onClick={onNeedClients}>
              Crear un cliente
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <Select label="Cliente *" value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">Selecciona un cliente</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>

            <Input
              label="Nombre de la sucursal *"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej. Sucursal Centro"
            />
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <Input
              label="Ciudad"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Ej. Tijuana"
            />
            <Input
              label="Teléfono"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Ej. 664-123-4567"
            />
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <Input
              label="Dirección"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Calle, número, colonia"
            />

            <Select label="Estado" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="active">Activa</option>
              <option value="inactive">Inactiva</option>
            </Select>
          </div>

          {paquetes.length > 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="mb-4 flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-white/70">Plan de pago</p>
                <span className="text-xs text-gray-400">
                  {paquetes.find((p) => Number(p.id) === Number(packageId))
                    ? `${formatMoneda(
                        paquetes.find((p) => Number(p.id) === Number(packageId)).amount,
                        paquetes.find((p) => Number(p.id) === Number(packageId)).currency
                      )} por paquete`
                    : "Puedes asignarlo después"}
                </span>
              </div>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                <Select label="Paquete" value={packageId} onChange={(e) => setPackageId(e.target.value)}>
                  <option value="">Sin paquete por ahora</option>
                  {paquetes.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} · {formatMoneda(p.amount, p.currency)}
                    </option>
                  ))}
                </Select>
                <Input
                  label="Fecha de pago"
                  type="date"
                  value={paymentDate}
                  disabled={!packageId}
                  onChange={(e) => {
                    setPaymentDate(e.target.value);
                    if (e.target.value && nextPaymentDate && e.target.value > nextPaymentDate) {
                      setNextPaymentDate(addDays(e.target.value, 30));
                    }
                  }}
                />
                <Input
                  label="Próximo pago"
                  type="date"
                  value={nextPaymentDate}
                  disabled={!packageId}
                  onChange={(e) => setNextPaymentDate(e.target.value)}
                />
              </div>
            </div>
          )}

          <Textarea
            label="Notas"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Notas internas (opcional)"
          />
        </>
      )}

      <FieldError message={serverError || error} />

      <div className="flex justify-end gap-3 border-t border-white/10 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        {clientes.length > 0 && (
          <Button type="submit" disabled={saving}>
            <IconCircleCheckFilled className="h-4 w-4 text-white" />
            {saving ? "Guardando..." : initial ? "Guardar cambios" : "Crear sucursal"}
          </Button>
        )}
      </div>
    </form>
  );
}